/**
 * New design token system aligned with Figma.
 *
 * Architecture: Primitives → Semantic → Components
 * - Primitives: raw values (color scales, spacing), never used directly in components
 * - Semantic: purpose-named aliases, the only layer components should consume
 *
 * Figma source: https://www.figma.com/design/mQn4u4MG0R255ynGoLowRJ/-WIP--Lemonade
 */

import { colorPrimitives } from './color-primitives'

// ===========================================================================
// LAYER 1 — PRIMITIVES (raw values, not consumed directly by components)
// ===========================================================================

// ---------------------------------------------------------------------------
// Color scales (50–900 ramp)
// ---------------------------------------------------------------------------

export const primaryScale = {
  primary50: '#fbf8ff',
  primary100: '#f4edff',
  primary200: '#dcc8ff',
  primary300: '#b48aff',
  primary400: '#8b4aff',
  primary500: '#7232e0',
  primary600: '#412276',
  primary700: '#2e1854',
  primary800: '#1f1038',
  primary900: '#110a1f',
} as const

export const neutralScale = {
  neutral50: '#f5f5f6',
  neutral100: '#f1f0f2',
  neutral200: '#e9e8eb',
  neutral300: '#d4d3d7',
  neutral400: '#b6b4b9',
  neutral500: '#949199',
  neutral600: '#6d6a73',
  neutral700: '#494550',
  neutral800: '#302c38',
  neutral900: '#0d0d0d',
} as const

export const successScale = {
  success50: '#f7fbfa',
  success100: '#dff6ec',
  success200: '#a8e6c9',
  success300: '#4cc990',
  success400: '#008850',
  success500: '#006e41',
  success600: '#005a35',
  success700: '#004d2d',
  success800: '#003d24',
  success900: '#002e1b',
} as const

export const warningScale = {
  warning50: '#fefbf8',
  warning100: '#fde3cc',
  warning200: '#facba0',
  warning300: '#f5a556',
  warning400: '#e77309',
  warning500: '#c46207',
  warning600: '#a45206',
  warning700: '#8a4405',
  warning800: '#6e3704',
  warning900: '#522903',
} as const

export const errorScale = {
  error50: '#fef8f8',
  error100: '#fce2e3',
  error200: '#f8b4b7',
  error300: '#f07e83',
  error400: '#e71a22',
  error500: '#b8141a',
  error600: '#991117',
  error700: '#8a0f14',
  error800: '#6e0c10',
  error900: '#52090c',
} as const

export const colorScale = {
  ...primaryScale,
  ...neutralScale,
  ...successScale,
  ...warningScale,
  ...errorScale,
  white: '#ffffff',
  black: '#0d0d0d',
} as const

// ---------------------------------------------------------------------------
// Spacing scale (base-4, in px)
// ---------------------------------------------------------------------------

export const spacingScale = {
  0: '0px',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
} as const

// ---------------------------------------------------------------------------
// Radius primitives (in px)
// ---------------------------------------------------------------------------

export const radiusScale = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
} as const

// ===========================================================================
// LAYER 2 — SEMANTIC TOKENS (the only layer components should consume)
// ===========================================================================

// ---------------------------------------------------------------------------
// Semantic colors
//
// IMPORTANT: These currently alias the LEGACY colorPrimitives so that
// migrating a component from `theme.colors.X` → `theme.semantic.Y` produces
// zero visual diff. Once all components are migrated, swap the aliases to
// point at the new `colorScale` values (Figma source of truth).
// ---------------------------------------------------------------------------

export const semanticColors = {
  // Content (text & icons)
  contentPrimary: colorPrimitives.text_primary,
  contentSecondary: colorPrimitives.gray600,
  contentTertiary: colorPrimitives.neutral300,
  contentPlaceholder: colorPrimitives.gray500,
  contentDisabled: colorPrimitives.gray100,
  contentInvert: colorPrimitives.white,
  contentBrand: colorPrimitives.purple400,
  contentBrandHover: primaryScale.primary600,
  // Text and icons on a brand surface (interactiveDefault / backgroundBrand).
  contentOnBrand: colorPrimitives.white,
  contentInfo: colorPrimitives.info400,
  contentOnInfo: colorPrimitives.info400,
  contentSuccess: colorPrimitives.success400,
  contentOnSuccess: colorPrimitives.success400,
  contentWarning: colorPrimitives.warning400,
  contentOnWarning: colorPrimitives.warning400,
  contentError: colorPrimitives.alert400,
  contentOnError: colorPrimitives.alert400,

  // Background
  backgroundPrimary: colorPrimitives.primarySurface,
  backgroundSecondary: colorPrimitives.white,
  backgroundTertiary: colorPrimitives.gray70,
  backgroundHover: colorPrimitives.gray70,
  backgroundDisabled: colorPrimitives.gray70,
  backgroundPrimaryInvert: colorPrimitives.black,
  backgroundSecondaryInvert: colorPrimitives.neutral600,
  backgroundBrand: colorPrimitives.purple400,
  backgroundBrandHover: primaryScale.primary600,
  backgroundBrandSubtle: colorPrimitives.purple100,
  backgroundAi: '#fffbfe',
  backgroundInfo: colorPrimitives.info100,
  backgroundInfoSubtle: colorPrimitives.info30,
  backgroundSuccess: colorPrimitives.success100,
  backgroundSuccessSubtle: colorPrimitives.success30,
  backgroundWarning: colorPrimitives.warning100,
  backgroundWarningSubtle: colorPrimitives.warning30,
  backgroundError: colorPrimitives.alert100,
  backgroundErrorSubtle: colorPrimitives.alert30,

  // Border
  borderDefault: colorPrimitives.gray90,
  borderSubtle: colorPrimitives.gray70,
  borderStrong: colorPrimitives.gray100,
  borderDisabled: colorPrimitives.gray80,
  borderBrand: colorPrimitives.purple400,
  borderInfo: colorPrimitives.info100,
  borderSuccess: colorPrimitives.success100,
  borderWarning: colorPrimitives.warning100,
  borderError: colorPrimitives.alert100,

  // Interactive (action buttons, links, controls)
  interactiveDefault: colorPrimitives.purple400,
  interactiveHover: primaryScale.primary600,
  interactiveActive: primaryScale.primary600,
  interactiveFocus: colorPrimitives.purple400,
  // Figma `interactive/secondary-hover`
  interactiveSecondaryHover: colorPrimitives.gray90,
  interactiveDisabled: colorPrimitives.gray100,
  interactiveDestructiveDefault: colorPrimitives.alert400,
  interactiveDestructiveHover: colorPrimitives.alert400,
} as const

// ---------------------------------------------------------------------------
// Semantic spacing (t-shirt aliases → numeric scale)
// ---------------------------------------------------------------------------

export const spacing = {
  0: spacingScale[0],
  xxxs: spacingScale[0.5],
  xxs: spacingScale[1],
  xs: spacingScale[2],
  s: spacingScale[3],
  m: spacingScale[4],
  l: spacingScale[6],
  xl: spacingScale[8],
  xxl: spacingScale[10],
} as const

// ---------------------------------------------------------------------------
// Semantic radius
// ---------------------------------------------------------------------------

export const radius = {
  interactive: radiusScale.sm,
  informative: radiusScale.sm,
  container: radiusScale.md,
} as const

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const fontFamily = {
  sans: 'Inter, sans-serif',
} as const

export const fontWeight = {
  regular: 400,
  semibold: 600,
} as const

export interface TypographyToken {
  fontSize: string
  lineHeight: string
  letterSpacing: string
  fontWeight: number
}

export const typography = {
  h1: {
    fontSize: '36px',
    lineHeight: '44px',
    letterSpacing: '0px',
    fontWeight: fontWeight.semibold,
  },
  h2: {
    fontSize: '28px',
    lineHeight: '36px',
    letterSpacing: '0px',
    fontWeight: fontWeight.semibold,
  },
  bodyLg: {
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0px',
    fontWeight: fontWeight.regular,
  },
  bodyLgSemibold: {
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0px',
    fontWeight: fontWeight.semibold,
  },
  bodyMd: {
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    fontWeight: fontWeight.regular,
  },
  bodyMdSemibold: {
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    fontWeight: fontWeight.semibold,
  },
  bodySm: {
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '0px',
    fontWeight: fontWeight.regular,
  },
} as const

// ---------------------------------------------------------------------------
// Heights
// ---------------------------------------------------------------------------

export const heights = {
  control: '48px',
} as const

// ===========================================================================
// Aggregate exports
// ===========================================================================

/** Raw primitives — use for building new semantic tokens, not in components */
export const primitives = {
  colorScale,
  spacingScale,
  radiusScale,
} as const

/** Semantic tokens — the only layer components should consume */
export const tokens = {
  colors: semanticColors,
  spacing,
  radius,
  typography,
  fontFamily,
  fontWeight,
  heights,
} as const

export type SemanticColors = Record<keyof typeof semanticColors, string>
export type Spacing = typeof spacing
export type Radius = typeof radius
export type Typography = typeof typography
export type Tokens = typeof tokens
